import React, { useContext, useMemo } from "react";
import { createContext } from "react";
import { useParams } from "react-router";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { ErrorMessage } from "../components/ErrorMessage/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator/LoadingIndicator";
import { ApiRequestState, useAdminApiRequest } from "../hooks/useApiRequest";

interface TModelContext {
  business: ApiRequestState<CherryHubAdminApi.Business | null>;
}

const ModelContext = createContext<TModelContext | null>(null);

/**
 * Loads models from the api based on the presence of react-router path parameters. Example: if
 * the `businessId` path parameter is set, this component will display loading & error states and
 * then inject the business into the context once loaded.
 */
export const ModelContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { businessId } = useParams();

  const businessRequest = useAdminApiRequest(
    (apiClient) => {
      if (!businessId) {
        return null;
      }
      return apiClient.getBusiness(businessId);
    },
    [businessId]
  );

  const { isLoading, error, notFound } = useMemo(
    () => ({
      isLoading: !!businessId && businessRequest.isLoading,
      error: !!businessId ? businessRequest.error : null,
      notFound:
        !!businessId && !businessRequest.isLoading && !businessRequest.data,
    }),
    [businessId, businessRequest]
  );

  const context = useMemo(
    () => ({
      business: businessRequest,
    }),
    [businessRequest]
  );

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (error || notFound) {
    return (
      <ErrorMessage>
        An error was encounterd while loading the page
      </ErrorMessage>
    );
  }

  return (
    <ModelContext.Provider value={context}>{children}</ModelContext.Provider>
  );
};

/**
 * Returns the business loaded from the `:businessId` path parameter
 */
export const useBusinessContext = (): CherryHubAdminApi.Business => {
  const ctx = useContext(ModelContext);

  if (!ctx || !ctx.business.data) {
    throw new Error("Missing business context");
  }

  return ctx.business.data;
};
